/**
 * Mitarbeiter-Verwaltung.
 *
 * Phase A (Kapitel 3.1/20.3, Entscheidung #25): nicht mehr Chef-exklusiv.
 * Owner (Chef) UND Filialleitung (LocationRole.manager an mind. einem
 * Standort) dürfen Mitarbeiter anlegen/bearbeiten/löschen — Filialleitung
 * beschränkt auf die eigenen Standorte und darf dabei nur die Rolle
 * "Mitarbeiter" vergeben (nie Filialleitung/Owner). Das Backend erzwingt das
 * bereits (`requireCompanyOwnerOrLocationManager` + Scoping in users.ts);
 * diese Seite spiegelt dieselbe Einschränkung in der UI, damit niemand einen
 * Button sieht, der ohnehin nur mit 403 enden würde.
 *
 * `allowedLocationIds` (alt, optional, "leer = überall") ist raus —
 * `locationAssignments` (mind. 1 Standort Pflicht) ersetzt es vollständig.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useAuth,
  useCanManage,
  useCanManageAnyLocation,
  useManagedLocationIds,
  deriveDisplayRole,
  DISPLAY_ROLE_META,
  type CompanyRole,
  type LocationRole,
  type UserRole,
} from '../store/auth';
import { usersApi, type ApiUser, type InviteResult, type LocationAssignmentInput } from '../lib/users';

/** v1.4.0: Rückmeldung zum Einladungs-Mailversand. */
function inviteNotice(result: InviteResult, email: string): { text: string; ok: boolean } {
  if (result === 'sent') return { text: `Einladung an ${email} verschickt.`, ok: true };
  if (result === 'not-configured') {
    return { text: 'Einladung nicht verschickt: Auf dem Server ist noch kein Mailversand eingerichtet (SMTP).', ok: false };
  }
  return { text: `Einladung an ${email} konnte nicht verschickt werden — bitte später erneut versuchen.`, ok: false };
}
import { locationsApi, type ApiLocation } from '../lib/locations';
import { formatMoney } from '../lib/format';
import { BREAK_DEDUCTION_FEATURE } from '../lib/payroll';
import { ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

const QK = ['users'] as const;

/**
 * Gesamtprüfung 11.09.2026: nach Änderungen an Mitarbeitern auch Dashboard
 * und Standort-Ansichten neu laden (Mitarbeiterzahl etc. blieb sonst bis zu
 * 30 s veraltet). ['users'] deckt per Präfix auch ['users', companyId] ab.
 */
function invalidateTeamViews(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: QK });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
}

/** Phase P (Kapitel 20.5, Entscheidung #26) — Anzeige-Label für die Beschäftigungsart. */
const EMPLOYMENT_TYPE_LABEL: Record<'minijob' | 'midijob' | 'parttime' | 'fulltime', string> = {
  minijob: 'Minijob',
  midijob: 'Midijob',
  parttime: 'Teilzeit',
  fulltime: 'Vollzeit',
};

// ---------------------------------------------------------------------------
// Scoping-Helfer (spiegeln die Backend-Prüfungen in backend/src/routes/users.ts)
// ---------------------------------------------------------------------------

/** Liegen ALLE Standort-Zuordnungen des Users (innerhalb der aktiven Firma) im übergebenen Set? */
function allLocationsWithin(
  u: ApiUser,
  activeCompanyId: string | null,
  allowedLocationIds: Set<string>,
): boolean {
  const relevant = u.locationMemberships.filter((m) => m.companyId === activeCompanyId);
  return relevant.length > 0 && relevant.every((m) => allowedLocationIds.has(m.locationId));
}

/** Ist der User (firmenübergreifend) irgendwo Owner/Alt-Manager oder Filialleitung? (spiegelt die DELETE-Sperre im Backend) */
function isOwnerOrManagerAnywhere(u: ApiUser): boolean {
  return (
    u.memberships.some((m) => m.role === 'owner' || m.role === 'manager') ||
    u.locationMemberships.some((m) => m.role === 'manager')
  );
}

export default function UsersAdminPage() {
  const token = useAuth((s) => s.token)!;
  const me = useAuth((s) => s.user)!;
  const activeCompanyId = useAuth((s) => s.activeCompanyId);
  const isOwner = useCanManage();
  const canEdit = useCanManageAnyLocation();
  const managedLocationIds = useManagedLocationIds();
  const managedSet = useMemo(() => new Set(managedLocationIds), [managedLocationIds]);
  const qc = useQueryClient();
  // v1.4.0: Hinweis nach Anlegen/Einladen (Mailversand).
  const [notice, setNotice] = useState<{ text: string; ok: boolean } | null>(null);

  const { data: users, isLoading, error } = useQuery({
    queryKey: QK,
    queryFn: () => usersApi.list(token),
  });

  const [editing, setEditing] = useState<ApiUser | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  // v1.3.0 (Shadow: „Mitarbeiter-Übersicht direkt nach Standorten sortieren"):
  // oben Chef/Admin, dann je Standort (in der Reihenfolge der Standort-Liste)
  // alle Personen mit diesem HAUPT-Standort — jede Person genau einmal, weitere
  // Standorte stehen in ihrer Zeile. Innerhalb: Filialleitung zuerst, dann A–Z.
  const { data: locationList } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationsApi.list(token),
  });
  const groups = useMemo(() => {
    type Group = { key: string; label: string; color: string | null; users: ApiUser[]; order: number };
    const locOrder = new Map<string, number>((locationList ?? []).map((l: ApiLocation, i: number) => [l.id, i]));
    const locColor = new Map<string, string>((locationList ?? []).map((l: ApiLocation) => [l.id, l.color]));
    const byKey = new Map<string, Group>();
    const chefGroup: Group = { key: 'chef', label: 'Chef & Verwaltung', color: null, users: [], order: -1 };
    const noneGroup: Group = { key: 'none', label: 'Ohne Standort', color: null, users: [], order: 1e9 };
    for (const u of users ?? []) {
      const role = deriveDisplayRole(u, activeCompanyId);
      // „Alle Firmen" (nur Admin): alle Zuordnungen berücksichtigen.
      const own = activeCompanyId
        ? u.locationMemberships.filter((m) => m.companyId === activeCompanyId)
        : u.locationMemberships;
      if (role === 'owner' || role === 'admin') {
        chefGroup.users.push(u);
        continue;
      }
      const primary = own.find((m) => m.isPrimary) ?? own[0];
      if (!primary) {
        noneGroup.users.push(u);
        continue;
      }
      let g: Group | undefined = byKey.get(primary.locationId);
      if (!g) {
        g = {
          key: primary.locationId,
          label: primary.locationName,
          color: locColor.get(primary.locationId) ?? null,
          users: [],
          order: locOrder.get(primary.locationId) ?? 1e6,
        };
        byKey.set(primary.locationId, g);
      }
      g.users.push(u);
    }
    const isManagerAt = (u: ApiUser, locationId: string) =>
      u.locationMemberships.some((m) => m.locationId === locationId && m.role === 'manager');
    const all = [chefGroup, ...byKey.values(), noneGroup].filter((g) => g.users.length > 0);
    for (const g of all) {
      g.users.sort((a, b) => {
        const am = isManagerAt(a, g.key) ? 0 : 1;
        const bm = isManagerAt(b, g.key) ? 0 : 1;
        if (am !== bm) return am - bm;
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return a.name.localeCompare(b.name, 'de');
      });
    }
    return all.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'de'));
  }, [users, activeCompanyId, locationList]);

  const renderUser = (u: ApiUser) => {
    const displayRole = deriveDisplayRole(u, activeCompanyId);
    const roleMeta = DISPLAY_ROLE_META[displayRole];
    // v1.3.0: Admin-Konten kann niemand löschen/sperren; bearbeiten nur ein
    // globaler Admin (spiegelt assertCanManageTarget/assertNotAdminAccount).
    const isAdminAccount = u.role === 'admin' || u.role === 'superadmin';
    const meIsGlobalAdmin = me.role === 'admin' || me.role === 'superadmin';
    // Gesamtprüfung 11.09.2026: in „Alle Firmen“ (Admin) kennt das Formular
    // keine Firma — Standort-Zuordnungen/Rollen würden falsch überschrieben.
    // Bearbeiten deshalb nur mit gewählter Firma (Chef-Ansicht).
    const companyContextOk = activeCompanyId !== null;
    const canEditThisUser =
      companyContextOk &&
      (!isAdminAccount || meIsGlobalAdmin) &&
      (isOwner ||
      (canEdit &&
        allLocationsWithin(u, activeCompanyId, managedSet) &&
        // Spiegelt assertCanManageTarget im Backend (seit Audit 11.09.2026
        // auch für PUT): keine Owner/Filialleitungen — firmenübergreifend.
        !isOwnerOrManagerAnywhere(u)));
    const canDeleteThisUser =
      companyContextOk &&
      u.id !== me.id &&
      !isAdminAccount &&
      (isOwner ||
        (canEdit &&
          !isOwnerOrManagerAnywhere(u) &&
          allLocationsWithin(u, activeCompanyId, managedSet)));
    // Phase U+Z (Kapitel 20.3, Entscheidung #30/#35) — dieselbe Scoping-
    // Regel wie beim Löschen: Owner darf jeden, Filialleitung nur
    // Mitarbeiter an eigenen Standorten ohne Filialleiter-/Owner-Rechte.
    const canDeactivateThisUser = canDeleteThisUser;
    const ownLocations = u.locationMemberships.filter((m) => m.companyId === activeCompanyId);

    return (
      <li
        key={u.id}
        className="bg-slate-800 rounded p-3 flex items-center justify-between gap-3"
      >
        <div className="min-w-0">
          <p className="font-medium truncate">
            {u.name}{' '}
            <span className={`text-xs rounded px-1.5 py-0.5 ${roleMeta.cls}`}>
              {roleMeta.label}
            </span>
            {/* Phase U+Z (Kapitel 20.3, Entscheidung #30/#35) */}
            {!u.isActive && (
              <span className="ml-1 text-xs rounded px-1.5 py-0.5 bg-slate-600/60 text-slate-300">
                Deaktiviert
              </span>
            )}
            {/* Phase HH (Kapitel 20.6, Entscheidung #43) — Löschantrag lief über
                "Account löschen" im eigenen Profil, nicht über diese Verwaltung. */}
            {u.deletionScheduledFor && (
              <span className="ml-1 text-xs rounded px-1.5 py-0.5 bg-red-900/60 text-red-300">
                Löschung am {new Date(u.deletionScheduledFor).toLocaleDateString('de-DE')}
              </span>
            )}
            {/* Kapitel 3.2 (Screen 7): noch kein eigenes Passwort festgelegt */}
            {u.passwordIsInitial && (
              <span className="ml-1 text-xs rounded px-1.5 py-0.5 bg-amber-900/50 text-amber-300">
                Erstanmeldung offen
              </span>
            )}
            {u.id === me.id && <span className="ml-2 text-xs text-emerald-400">(du)</span>}
          </p>
          <p className="text-sm text-slate-400 truncate">{u.email}</p>
          <p className="text-sm">
            Stundenlohn: {formatMoney(u.hourlyWage)}{' '}
            {meIsGlobalAdmin && (
              <span
                className="text-xs text-slate-500"
                title="Löhne sind für Admins verschlüsselt — sichtbar nur für den Chef und die Person selbst"
              >
                (verschlüsselt){' '}
              </span>
            )}
            <span className="text-xs rounded px-1.5 py-0.5 bg-slate-700 text-slate-300">
              {EMPLOYMENT_TYPE_LABEL[u.employmentType]}
            </span>
          </p>
          {ownLocations.length > 0 && (
            <p className="text-xs text-slate-500 truncate">
              {ownLocations
                .map((lm) => lm.locationName + (lm.role === 'manager' ? ' (Filialleitung)' : ''))
                .join(', ')}
            </p>
          )}
        </div>
        {(canEditThisUser || canDeleteThisUser) && (
          <div className="flex flex-col gap-1 shrink-0">
            {canEditThisUser && (
              <button
                onClick={() => setEditing(u)}
                className="text-sm bg-slate-700 hover:bg-slate-600 rounded px-3 py-1"
              >
                Bearbeiten
              </button>
            )}
            {canEditThisUser && u.passwordIsInitial && u.isActive && u.id !== me.id && (
              <InviteButton user={u} token={token} onResult={setNotice} />
            )}
            {canDeactivateThisUser && <DeactivateButton user={u} token={token} qc={qc} />}
            {canDeleteThisUser && <DeleteButton user={u} token={token} qc={qc} />}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="max-w-2xl lg:max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Mitarbeiter</h2>
        {canEdit && activeCompanyId !== null && (
          <button
            onClick={() => setShowCreate(true)}
            className="bg-brand hover:bg-brand-dark transition rounded px-3 py-2 text-sm"
          >
            + Neuer Mitarbeiter
          </button>
        )}
      </div>
      {!canEdit && (
        <p className="text-xs text-slate-500">
          Schreibrechte haben nur Chef (Owner) oder Filialleitung. Du siehst die Mitarbeiter zur
          Übersicht (z. B. für den Wochenplan).
        </p>
      )}
      {canEdit && !isOwner && (
        <p className="text-xs text-slate-500">
          Als Filialleitung siehst und verwaltest du nur Mitarbeiter an deinen eigenen Standorten
          und darfst dabei nur die Rolle „Mitarbeiter" vergeben.
        </p>
      )}

      {notice && (
        <div
          className={`text-sm rounded px-3 py-2 flex items-start justify-between gap-2 border ${
            notice.ok
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
          }`}
        >
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} className="text-xs opacity-70 hover:opacity-100 !min-h-0" aria-label="Schließen">
            ✕
          </button>
        </div>
      )}
      {isLoading && <p className="text-slate-400">Lade…</p>}
      {error && <p className="text-red-400">Fehler beim Laden: {(error as Error).message}</p>}
      {activeCompanyId === null && (
        <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2">
          „Alle Firmen“ ist gewählt — zum Bearbeiten oben eine Firma auswählen (Chef-Ansicht).
        </p>
      )}

      {groups.map((g) => (
        <section key={g.key} className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2 px-1 pt-2">
            {g.color && (
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: g.color }} aria-hidden />
            )}
            {g.label}
            <span className="text-xs font-normal text-slate-500">
              {g.users.length} {g.users.length === 1 ? 'Person' : 'Personen'}
            </span>
          </h3>
          <ul className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-3 lg:space-y-0">
            {g.users.map((u) => renderUser(u))}
          </ul>
        </section>
      ))}

      {showCreate && (
        <UserFormModal
          mode="create"
          onClose={() => setShowCreate(false)}
          onSaved={(n) => {
            setShowCreate(false);
            setNotice(n ?? null);
            invalidateTeamViews(qc);
          }}
        />
      )}
      {editing && (
        <UserFormModal
          mode="edit"
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidateTeamViews(qc);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Phase U+Z (Kapitel 20.3, Entscheidung #30/#35) — "Deaktivieren" statt
 * Löschen für den Alltagsfall kurzfristiger Weggang: historische
 * Schichten/Lohn-Daten bleiben erhalten, der Zugang wird gesperrt. Vor dem
 * eigentlichen Deaktivieren wird die Anzahl künftiger geplanter Schichten
 * abgefragt und im Bestätigungsdialog angezeigt (kein automatisches
 * Umbesetzen — läuft über den ohnehin editierbaren Schichtplan, Kapitel
 * 19.7). "Reaktivieren" togglet denselben Zustand zurück, ohne separaten
 * Flow.
 */
function DeactivateButton({
  user,
  token,
  qc,
}: {
  user: ApiUser;
  token: string;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [busy, setBusy] = useState(false);
  const deactivate = useMutation({
    mutationFn: () => usersApi.deactivate(token, user.id),
    onSuccess: () => invalidateTeamViews(qc),
    onError: (err: unknown) => {
      if (err instanceof ApiError) alert(err.message);
      else if (err instanceof Error) alert(err.message);
    },
  });
  const reactivate = useMutation({
    mutationFn: () => usersApi.reactivate(token, user.id),
    onSuccess: () => invalidateTeamViews(qc),
    onError: (err: unknown) => {
      if (err instanceof ApiError) alert(err.message);
      else if (err instanceof Error) alert(err.message);
    },
  });

  if (user.isActive) {
    async function onDeactivateClick() {
      setBusy(true);
      try {
        const count = await usersApi.futureShiftCount(token, user.id);
        const warning =
          count > 0
            ? `Achtung: ${count} künftige Schicht${count === 1 ? '' : 'en'} sind noch "${user.name}" zugeordnet — die Umbesetzung läuft über den Schichtplan.

`
            : '';
        if (confirm(`${warning}"${user.name}" wirklich deaktivieren? Der Zugang wird gesperrt, alle Daten bleiben erhalten.`)) {
          deactivate.mutate();
        }
      } catch (err) {
        if (err instanceof ApiError) alert(err.message);
      } finally {
        setBusy(false);
      }
    }
    return (
      <button
        onClick={() => void onDeactivateClick()}
        className="text-sm bg-amber-600/80 hover:bg-amber-600 rounded px-3 py-1"
        disabled={busy || deactivate.isPending}
      >
        {busy || deactivate.isPending ? '…' : 'Deaktivieren'}
      </button>
    );
  }

  return (
    <button
      onClick={() => {
        const deletionNote = user.deletionScheduledFor
          ? ' Eine geplante Selbst-Löschung (Kapitel 20.6) wird dabei ebenfalls aufgehoben.'
          : '';
        if (confirm(`"${user.name}" wieder aktivieren?${deletionNote}`)) reactivate.mutate();
      }}
      className="text-sm bg-emerald-700/80 hover:bg-emerald-700 rounded px-3 py-1"
      disabled={reactivate.isPending}
    >
      {reactivate.isPending ? '…' : 'Reaktivieren'}
    </button>
  );
}

/** v1.4.0: Einladungs-Mail erneut schicken (offene Erstanmeldung). */
function InviteButton({
  user,
  token,
  onResult,
}: {
  user: ApiUser;
  token: string;
  onResult: (n: { text: string; ok: boolean }) => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      disabled={busy}
      onClick={async () => {
        if (!confirm(`Einladung an ${user.email} schicken? Ein früher verschickter Einladungs-Link wird dabei ungültig.`)) return;
        setBusy(true);
        try {
          const result = await usersApi.invite(token, user.id);
          if (result === 'sent') hapticSuccess();
          onResult(inviteNotice(result, user.email));
        } catch (err) {
          hapticError();
          onResult({ text: err instanceof ApiError ? err.message : 'Einladung fehlgeschlagen', ok: false });
        } finally {
          setBusy(false);
        }
      }}
      className="text-sm bg-slate-700 hover:bg-slate-600 rounded px-3 py-1 disabled:opacity-50"
    >
      {busy ? '…' : 'Einladen'}
    </button>
  );
}

function DeleteButton({
  user,
  token,
  qc,
}: {
  user: ApiUser;
  token: string;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const m = useMutation({
    mutationFn: () => usersApi.remove(token, user.id),
    onSuccess: () => invalidateTeamViews(qc),
    onError: (err: unknown) => {
      if (err instanceof ApiError) alert(err.message);
      else if (err instanceof Error) alert(err.message);
    },
  });
  return (
    <button
      onClick={() => {
        if (confirm(`Mitarbeiter "${user.name}" wirklich löschen?`)) m.mutate();
      }}
      className="text-sm bg-red-600/80 hover:bg-red-600 rounded px-3 py-1"
      disabled={m.isPending}
    >
      Löschen
    </button>
  );
}

// ---------------------------------------------------------------------------

interface FormProps {
  mode: 'create' | 'edit';
  user?: ApiUser;
  onClose: () => void;
  /** v1.4.0: optional mit Hinweis zum Einladungs-Mailversand (nur beim Anlegen). */
  onSaved: (notice?: { text: string; ok: boolean }) => void;
  /**
   * Nachtrag (Standort-Detail-Ansicht, 08.09.2026): beim Anlegen direkt von
   * einer Standort-Seite aus vorausgewählter Standort (als Mitarbeiter) —
   * erspart das erneute Auswählen des Standorts, von dem aus man kam. Nur
   * für `mode: 'create'` relevant.
   */
  defaultLocationId?: string;
}

/** Ein per Checkbox ausgewählter Standort + (nur für Owner wählbare) Rolle dort. */
interface AssignmentRow {
  locationId: string;
  role: LocationRole;
}

export function UserFormModal({ mode, user, onClose, onSaved, defaultLocationId }: FormProps) {
  const token = useAuth((s) => s.token)!;
  const me = useAuth((s) => s.user)!;
  const activeCompanyId = useAuth((s) => s.activeCompanyId);
  const isOwner = useCanManage();
  const managedLocationIds = useManagedLocationIds();
  const meIsGlobalAdmin = me.role === 'admin' || me.role === 'superadmin';
  // Entscheidung #19d: niemand darf den eigenen Lohn selbst ändern — auch nicht
  // der Chef über diese Verwaltungsseite an seinem eigenen Datensatz. Das
  // Backend lehnt das ohnehin ab (PUT /api/users/:id); hier blenden wir die
  // Felder zusätzlich aus, statt den User erst beim Speichern auflaufen zu lassen.
  const isSelf = mode === 'edit' && user?.id === me.id;
  // Phase G (Entscheidung #6): Backend liefert hourlyWage/monthlySalary als
  // `null`, wenn diese Filialleitung an KEINEM gemeinsamen Standort
  // managerCanSeeWages hat — Kapitel 20.3 erlaubt das Bearbeiten trotzdem
  // (Chef überall, Filialleitung an eigenen Standorten, unabhängig vom
  // Sichtbarkeits-Flag), also lassen wir das Formular leer statt "null"
  // anzuzeigen und schicken die Felder beim Speichern nur mit, wenn
  // tatsächlich ein neuer Wert eingetragen wurde (leer = unverändert).
  const wageHiddenForEditor = mode === 'edit' && !isSelf && user != null && user.hourlyWage === null;
  // v1.3.0: Admins sehen Löhne nur als 0,00 (Server maskiert) und können sie
  // weder setzen noch ändern — das Backend verwirft die Felder ohnehin.
  const wageLockedForAdmin = meIsGlobalAdmin;

  const [email, setEmail] = useState(user?.email ?? '');
  const [name, setName] = useState(user?.name ?? '');
  const [password, setPassword] = useState('');
  // v1.4.0: Einladungs-Mail beim Anlegen (Standard: an).
  const [sendInvite, setSendInvite] = useState(true);
  const [systemRole, setSystemRole] = useState<UserRole>(user?.role ?? 'user');
  const [hourlyWage, setHourlyWage] = useState<string>(
    // v1.3.0: vom Admin angelegte Konten starten mit 1,00 € (setzt der Server).
    mode === 'create' ? (wageLockedForAdmin ? '1.00' : '14.50') : user?.hourlyWage != null ? String(user.hourlyWage) : '',
  );
  const [breakDeductionEnabled, setBreakDeduction] = useState<boolean>(
    user?.breakDeductionEnabled ?? false,
  );
  const [billingCycleStartDay, setCycleStart] = useState<string>(
    String(user?.billingCycleStartDay ?? 1),
  );
  const [salaryMode, setSalaryMode] = useState<'hourly' | 'salary'>(
    user?.salaryMode ?? 'hourly',
  );
  const [employmentType, setEmploymentType] = useState<
    'minijob' | 'midijob' | 'parttime' | 'fulltime'
  >(user?.employmentType ?? 'fulltime');
  const [monthlySalary, setMonthlySalary] = useState<string>(
    mode === 'create' ? '0' : user?.monthlySalary != null ? String(user.monthlySalary) : '',
  );
  const [monthlyTargetHours, setMonthlyTargetHours] = useState<string>(
    user ? String(user.monthlyTargetHours) : '0',
  );
  const [assignments, setAssignments] = useState<AssignmentRow[]>(() => {
    if (user) {
      return (user.locationMemberships ?? [])
        .filter((m) => m.companyId === activeCompanyId)
        .map((m) => ({ locationId: m.locationId, role: m.role }));
    }
    // Nachtrag (Standort-Detail-Ansicht, 08.09.2026): direkt von einer
    // Standort-Seite angelegt → diesen Standort gleich vorauswählen.
    return defaultLocationId ? [{ locationId: defaultLocationId, role: 'employee' as LocationRole }] : [];
  });
  // Gesamtprüfung 11.09.2026 (kritisch): auch der Alt-Wert 'manager' ist
  // Chef — vorher startete das Häkchen für Chefs, die ihre Firma selbst
  // registriert hatten, leer, und schon das Speichern des eigenen Namens
  // stufte sie zur Mitarbeiterin herab.
  const initialOwner = (user?.memberships ?? []).some(
    (m) => m.companyId === activeCompanyId && (m.role === 'owner' || m.role === 'manager'),
  );
  const [makeOwner, setMakeOwner] = useState(initialOwner);
  // Nachtrag 11.09.2026: Tagesgehalt aus der Kasse entnehmen (nur Chef schaltet frei).
  const [canTakeDailyPay, setCanTakeDailyPay] = useState(!!user?.canTakeDailyPay);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Alle Standorte der aktiven Firma laden — Filialleitung sieht/wählt nur die eigenen.
  const { data: allLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationsApi.list(token),
  });
  const selectableLocations: ApiLocation[] = useMemo(() => {
    if (!allLocations) return [];
    if (isOwner) return allLocations;
    const managed = new Set(managedLocationIds);
    return allLocations.filter((l) => managed.has(l.id));
  }, [allLocations, isOwner, managedLocationIds]);

  // Gesamtprüfung 11.09.2026: eine Filialleitung ohne Lohn-Freigabe
  // (managerCanSeeWages an keinem gewählten Standort) setzt auch beim
  // Anlegen keinen Lohn — der Server übernimmt dann den Standard (14,50 €),
  // den echten Lohn trägt der Chef ein.
  const payLockedForCreate =
    mode === 'create' &&
    !isOwner &&
    !assignments.some((a) => allLocations?.find((l) => l.id === a.locationId)?.managerCanSeeWages);
  const payLocked = wageLockedForAdmin || payLockedForCreate;

  function toggleLocation(id: string) {
    setAssignments((prev) => {
      if (prev.some((a) => a.locationId === id)) {
        return prev.filter((a) => a.locationId !== id);
      }
      return [...prev, { locationId: id, role: 'employee' }];
    });
  }

  function setLocationRole(id: string, role: LocationRole) {
    // Filialleitung darf nur "Mitarbeiter" vergeben — wird zusätzlich beim
    // Submit nochmal erzwungen, hier nur UI-seitig gar nicht erst anbieten.
    if (!isOwner) return;
    setAssignments((prev) => prev.map((a) => (a.locationId === id ? { ...a, role } : a)));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (assignments.length === 0) {
      setError('Mindestens ein Standort ist erforderlich.');
      return;
    }
    setBusy(true);
    try {
      const cycle = parseInt(billingCycleStartDay, 10);
      if (!Number.isFinite(cycle) || cycle < 1 || cycle > 28) {
        throw new Error('Abrechnungs-Start-Tag muss zwischen 1 und 28 liegen.');
      }
      const locationAssignments: LocationAssignmentInput[] = assignments.map((a) => ({
        locationId: a.locationId,
        // Filialleitung darf ausschließlich "employee" senden — das Backend
        // lehnt jede andere Rolle in ihrem Request ohnehin ab (403).
        role: isOwner ? a.role : 'employee',
      }));
      const payload: Record<string, unknown> = {
        email,
        name,
        breakDeductionEnabled,
        billingCycleStartDay: cycle,
        locationAssignments,
      };
      // Entscheidung #19d: den eigenen Lohn schicken wir gar nicht erst mit —
      // das Backend würde es ohnehin ablehnen, aber so bleibt es ein stilles
      // "wird nicht geändert" statt eines Fehlers beim Speichern.
      if (!isSelf) {
        // Verborgener Lohn (wageHiddenForEditor) + leer gelassenes Feld →
        // gar nicht mitschicken, damit der bestehende Wert unangetastet
        // bleibt (das Backend ändert nur explizit mitgeschickte Felder).
        const hourlyWageTrimmed = hourlyWage.trim();
        if (payLocked || (wageHiddenForEditor && hourlyWageTrimmed === '')) {
          // unverändert lassen
        } else {
          const wage = Number(hourlyWageTrimmed.replace(',', '.'));
          if (Number.isNaN(wage) || wage < 0) {
            throw new Error('Stundenlohn ungültig');
          }
          payload.hourlyWage = wage;
        }
        payload.salaryMode = salaryMode;
        if (salaryMode === 'salary') {
          const target = Number(monthlyTargetHours.replace(',', '.'));
          if (!Number.isFinite(target) || target < 0) {
            throw new Error('Sollstunden ungültig');
          }
          payload.monthlyTargetHours = target;
          const monthlySalaryTrimmed = monthlySalary.trim();
          if (payLocked || (wageHiddenForEditor && monthlySalaryTrimmed === '')) {
            // unverändert lassen
          } else {
            const salary = Number(monthlySalaryTrimmed.replace(',', '.'));
            if (!Number.isFinite(salary) || salary < 0) {
              throw new Error('Festgehalt ungültig');
            }
            payload.monthlySalary = salary;
          }
        } else {
          if (!payLocked) payload.monthlySalary = 0;
          payload.monthlyTargetHours = 0;
        }
      }
      // Beschäftigungsart (Phase P, Entscheidung #26) — rein informativ, immer
      // mitgeschickt wenn nicht die Selbstbearbeitungs-Ansicht aktiv ist (siehe
      // isSelf-Zweig oben, der diese Felder gar nicht rendert).
      if (!isSelf) {
        payload.employmentType = employmentType;
      }
      // Nur Owner/Admin dürfen companyRole überhaupt mitschicken.
      if (isOwner) {
        // Nur mitschicken, wenn sich die Chef-Rolle wirklich ändert (bzw. beim
        // Anlegen als Chef) — nie an der eigenen Person.
        if (!isSelf && (mode === 'create' ? makeOwner : makeOwner !== initialOwner)) {
          payload.companyRole = (makeOwner ? 'owner' : 'employee') as CompanyRole;
        }
        payload.canTakeDailyPay = canTakeDailyPay;
      }
      // Nur ein globaler Admin darf die System-Rolle setzen.
      if (meIsGlobalAdmin) {
        payload.role = systemRole;
      }
      if (mode === 'create') {
        // Kapitel 3.2: leeres Passwort = Erstanmeldung mit dem Filialpasswort.
        const created = await usersApi.create(token, {
          ...payload,
          ...(password ? { password } : {}),
          sendInvite,
        } as never);
        onSaved(created.invite ? inviteNotice(created.invite, email) : undefined);
        return;
      } else if (user) {
        if (password && !isSelf) payload.password = password;
        await usersApi.update(token, user.id, payload);
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
      else setError('Unbekannter Fehler');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {mode === 'create' ? 'Neuen Mitarbeiter anlegen' : 'Mitarbeiter bearbeiten'}
        </h3>
        <form onSubmit={onSubmit} className="space-y-3">
          <Field label="Name">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="E-Mail">
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={isSelf}
              title={isSelf ? 'Deine eigene E-Mail änderst du nicht hier' : undefined}
              className="input"
            />
          </Field>
          {!isSelf && (
          <Field label={mode === 'create' ? 'Startpasswort (optional)' : 'Neues Passwort (leer = unverändert)'}>
            <input
              type="password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              autoComplete="new-password"
              aria-describedby="password-help"
            />
            {/* Kapitel 3.2 (Screen 7) */}
            <p id="password-help" className="text-[11px] text-slate-500 mt-1">
              {mode === 'create'
                ? 'Leer lassen: Die erste Anmeldung läuft mit dem Filialpasswort des Haupt-Standorts (nicht für Chef/Filialleitung — die brauchen ein Startpasswort). In beiden Fällen legt die Person danach ihr eigenes Passwort fest.'
                : 'Die Person wird überall abgemeldet und muss dieses Passwort beim nächsten Login durch ein eigenes ersetzen.'}
            </p>
          </Field>
          )}
          {mode === 'create' && (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                className="mt-1"
              />
              <span>
                Einladung per E-Mail schicken
                <span className="block text-[11px] text-slate-500">
                  Mit Link, über den die Person selbst ihr Passwort festlegt (7 Tage gültig). Das Startpasswort steht
                  nicht in der Mail.
                </span>
              </span>
            </label>
          )}
          {isSelf && (
            <p className="text-[11px] text-slate-500 -mt-1">
              Dein eigenes Passwort und deine E-Mail änderst du im Profil (mit deinem aktuellen Passwort).
            </p>
          )}

          {meIsGlobalAdmin && (
            <Field label="System-Rolle (nur globaler Admin — nicht die Standort-/Firmen-Rolle unten)">
              <select
                value={systemRole}
                onChange={(e) => setSystemRole(e.target.value as UserRole)}
                className="input"
              >
                <option value="user">Standard (Rechte laufen über Standorte/Firma)</option>
                <option value="admin">Admin</option>
                <option value="superadmin">Superadmin</option>
                {/* Alt-Werte (vor Phase A) — nur angezeigt, falls genau das gerade der
                    bestehende Wert dieses Users ist, damit der Select nicht leer/undefiniert wirkt. */}
                {systemRole === 'manager' && <option value="manager">Filialleitung (alt)</option>}
                {systemRole === 'employee' && <option value="employee">Mitarbeiter (alt)</option>}
              </select>
            </Field>
          )}

          {isSelf ? (
            <div className="rounded bg-slate-900/40 p-3 text-sm">
              <p className="text-slate-300">
                Stundenlohn: <span className="text-emerald-400">{hourlyWage} €</span>
                {salaryMode === 'salary'
                  ? ` · Festgehalt ${monthlySalary} € für ${monthlyTargetHours} Std.`
                  : ' · Reiner Stundenlohn'}
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                Deinen eigenen Lohn kannst du hier nicht ändern (Entscheidung #19d) — das muss
                eine andere Chef- oder Filialleitungs-Person übernehmen.
              </p>
            </div>
          ) : (
            <>
              {wageHiddenForEditor && (
                <p className="text-[11px] text-amber-300 -mb-1">
                  Der aktuelle Lohn dieser Person ist für dich nicht sichtbar (Standort-Einstellung,
                  Entscheidung #6). Leer lassen ändert nichts — nur ausfüllen, wenn du einen neuen
                  Wert setzen willst.
                </p>
              )}
              {payLockedForCreate && !wageLockedForAdmin && (
                <p className="text-[11px] text-slate-400 -mb-1">
                  Den Lohn legt der Chef fest (an diesen Standorten ist für die Filialleitung keine
                  Lohn-Einsicht freigegeben). Das Konto startet mit dem Standard-Stundenlohn.
                </p>
              )}
              {wageLockedForAdmin && (
                <p className="text-[11px] text-slate-400 -mb-1">
                  Löhne sind für Admins verschlüsselt und werden als 0,00 angezeigt. Stundenlohn,
                  Festgehalt und die übrigen Abrechnungs-Einstellungen setzt der Chef der Firma.
                  {mode === 'create' && ' Neue Konten starten mit einem Stundenlohn von 1,00 €.'}
                </p>
              )}
              <Field label="Stundenlohn (€) — gilt für neue Schichten + Überstunden">
                <input
                  required={!wageHiddenForEditor && !payLocked}
                  disabled={payLocked}
                  inputMode="decimal"
                  value={hourlyWage}
                  onChange={(e) => setHourlyWage(e.target.value)}
                  placeholder={wageHiddenForEditor ? 'unverändert lassen' : undefined}
                  className="input"
                />
              </Field>

              <Field label="Lohn-Modell">
                <select
                  value={salaryMode}
                  onChange={(e) => setSalaryMode(e.target.value as 'hourly' | 'salary')}
                  disabled={wageLockedForAdmin && mode === 'edit'}
                  className="input"
                >
                  <option value="hourly">Reiner Stundenlohn</option>
                  <option value="salary">Festgehalt + Überstunden-Lohn</option>
                </select>
              </Field>

              <Field label="Beschäftigungsart">
                <select
                  value={employmentType}
                  onChange={(e) =>
                    setEmploymentType(
                      e.target.value as 'minijob' | 'midijob' | 'parttime' | 'fulltime',
                    )
                  }
                  className="input"
                >
                  <option value="minijob">Minijob</option>
                  <option value="midijob">Midijob</option>
                  <option value="parttime">Teilzeit</option>
                  <option value="fulltime">Vollzeit</option>
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  Rein informativ — hat keinen Einfluss auf die Lohnberechnung
                  (die läuft weiterhin über Lohn-Modell/Stundenlohn/Festgehalt oben).
                  Nur bei „Minijob" zeigt die Übersicht den Fortschrittsbalken zur
                  Minijob-Grenze der Firma.
                </p>
              </Field>

              {salaryMode === 'salary' && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Festgehalt (€/Monat)">
                    <input
                      inputMode="decimal"
                      value={monthlySalary}
                      onChange={(e) => setMonthlySalary(e.target.value)}
                      disabled={payLocked}
                      placeholder={wageHiddenForEditor ? 'unverändert lassen' : undefined}
                      className="input"
                    />
                  </Field>
                  <Field label="Sollstunden / Monat">
                    <input
                      inputMode="decimal"
                      value={monthlyTargetHours}
                      onChange={(e) => setMonthlyTargetHours(e.target.value)}
                      disabled={wageLockedForAdmin && mode === 'edit'}
                      className="input"
                    />
                  </Field>
                </div>
              )}
            </>
          )}

          <Field label="Abrechnungs-Start (Tag 1–28)">
            <input
              type="number"
              min={1}
              max={28}
              inputMode="numeric"
              value={billingCycleStartDay}
              onChange={(e) => setCycleStart(e.target.value)}
              disabled={wageLockedForAdmin && mode === 'edit'}
              className="input"
            />
          </Field>

          {/* Shadow 11.09.2026: „Pause gibt es nicht“ — Pausen-Abzug ausgeblendet
              (Feld bleibt im Datensatz, siehe BREAK_DEDUCTION_FEATURE in lib/payroll.ts). */}
          {BREAK_DEDUCTION_FEATURE && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={breakDeductionEnabled}
              onChange={(e) => setBreakDeduction(e.target.checked)}
              disabled={wageLockedForAdmin && mode === 'edit'}
              className="w-4 h-4 accent-brand"
            />
            Gesetzlichen Pausen-Abzug aktivieren ({'>'}6 h: −30 min, {'>'}9 h: −45 min)
          </label>
          )}

          {isOwner && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={makeOwner}
                onChange={(e) => setMakeOwner(e.target.checked)}
                disabled={isSelf}
                className="w-4 h-4 accent-brand"
              />
              Chef (Owner) dieser Firma — z. B. für einen zweiten Chef
            </label>
          )}

          {isOwner && (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={canTakeDailyPay}
                onChange={(e) => setCanTakeDailyPay(e.target.checked)}
                className="w-4 h-4 accent-brand mt-0.5"
              />
              <span>
                Darf das Tagesgehalt selbst aus der Kasse nehmen
                <span className="block text-[11px] text-slate-500">
                  Die Schicht gilt dann sofort als abgerechnet, du bekommst eine Push. Nur bei Bezahlung nach Stunden.
                </span>
              </span>
            </label>
          )}

          <div>
            <span className="block text-sm text-slate-300 mb-1">
              Standorte (mind. 1 Pflicht){!isOwner && ' — nur deine eigenen'}
            </span>
            <div className="space-y-1.5">
              {selectableLocations.map((l) => {
                const row = assignments.find((a) => a.locationId === l.id);
                return (
                  <div key={l.id} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleLocation(l.id)}
                      className={`flex-1 min-w-0 text-left text-xs px-3 py-1.5 rounded truncate ${
                        row ? 'bg-brand text-white' : 'bg-slate-700 hover:bg-slate-600'
                      }`}
                    >
                      {l.name}
                    </button>
                    {row && isOwner && (
                      <select
                        value={row.role}
                        onChange={(e) => setLocationRole(l.id, e.target.value as LocationRole)}
                        className="text-xs bg-slate-900 border border-border rounded px-2 py-1 !min-h-0 shrink-0"
                      >
                        <option value="employee">Mitarbeiter</option>
                        <option value="manager">Filialleitung</option>
                      </select>
                    )}
                    {row && !isOwner && (
                      <span className="text-xs text-slate-500 shrink-0">Mitarbeiter</span>
                    )}
                  </div>
                );
              })}
              {selectableLocations.length === 0 && (
                <p className="text-xs text-slate-500">
                  {isOwner ? 'Noch keine Standorte angelegt.' : 'Du bist selbst noch keiner Filiale als Filialleitung zugeordnet.'}
                </p>
              )}
            </div>
            {assignments.length === 0 && (
              <p className="text-xs text-amber-400 mt-1">
                Mindestens ein Standort ist erforderlich.
              </p>
            )}
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm rounded bg-slate-700 hover:bg-slate-600"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-3 py-2 text-sm rounded bg-brand hover:bg-brand-dark disabled:opacity-50"
            >
              {busy ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm text-slate-300 mb-1">{label}</span>
      {children}
    </label>
  );
}
